import Header from "../components/Header";

const LAST_UPDATED = "8 May 2026";

function Section({ title, children }) {
  return (
    <div>
      <h2 className="text-2xl font-black text-[#ffd978] mb-3">{title}</h2>
      <div className="space-y-3 text-white/75 leading-relaxed">{children}</div>
    </div>
  );
}

function Sub({ title, children }) {
  return (
    <div className="mt-4">
      <h3 className="font-black text-white/90 mb-1">{title}</h3>
      <div className="text-white/70">{children}</div>
    </div>
  );
}

function List({ items }) {
  return (
    <ul className="list-disc pl-6 space-y-1">
      {items.map((item, i) => (
        <li key={i}>{item}</li>
      ))}
    </ul>
  );
}

export default function PrivacyPolicy() {
  return (
    <main className="bg-[#050505] text-white">
      <Header />

      <section className="mx-auto max-w-4xl px-6 pt-36 pb-20 select-text">
        <p className="font-black uppercase text-[#ffb56f]">Legal</p>
        <h1 className="outfall-heading text-[clamp(48px,8vw,90px)] leading-none text-[#ffd978] drop-shadow-[0_0_18px_#ff8f6b]">
          Privacy Policy
        </h1>
        <p className="mt-4 text-white/40 font-bold">
          Last updated: {LAST_UPDATED}
        </p>

        <div className="mt-12 space-y-10">
          <p className="text-white/70 leading-relaxed">
            This Privacy Policy explains how Outfall ("we", "us", "our")
            collects, uses, stores, and protects your personal data when you use
            our Roblox experience, website (
            <strong className="text-white/90">outfall.website</strong>), Discord
            community, or any related services. By using our services you agree
            to the practices described in this policy.
          </p>

          <Section title="1. Who We Are">
            <p>
              Outfall is an independent game development project operating the
              Roblox experience "Outfall" and associated website at
              outfall.website and outfall.co.uk. For data-related enquiries,
              contact us through our official Discord server or via the contact
              details listed at the end of this document.
            </p>
            <p>
              We are the data controller for personal data collected through our
              website and related services. Roblox Corporation is a separate and
              independent data controller for data collected through their
              platform.
            </p>
          </Section>

          <Section title="2. What Data We Collect">
            <Sub title="2.1 Discord OAuth Login">
              <p>
                When you sign in to our website using Discord, we receive and
                store:
              </p>
              <List
                items={[
                  "Discord user ID",
                  "Discord username and display name",
                  "Email address (if your Discord account has a verified email)",
                  "Avatar URL",
                  "Discord guild membership and role information (to verify staff access)",
                ]}
              />
              <p className="mt-2">
                We do not receive your Discord password. Authentication is
                handled entirely by Discord.
              </p>
            </Sub>
            <Sub title="2.2 Career Applications">
              <p>
                When you submit a career application on our website, we collect:
              </p>
              <List
                items={[
                  "All information you enter in the application form (name, answers, any uploaded files)",
                  "Your Discord ID and username (linked to your application)",
                  "Submission timestamp",
                ]}
              />
            </Sub>
            <Sub title="2.3 Ban Appeals">
              <p>When you submit a ban appeal, we collect:</p>
              <List
                items={[
                  "Your Discord ID and username",
                  "Your Roblox username",
                  "The reason for your ban (as you describe it)",
                  "Your appeal text",
                  "Any evidence links you provide",
                  "Staff decisions and notes",
                  "Submission and decision timestamps",
                ]}
              />
            </Sub>
            <Sub title="2.4 Technical Data">
              <p>
                Our hosting provider (Vercel) and infrastructure may
                automatically log:
              </p>
              <List
                items={[
                  "IP addresses",
                  "Browser type and version",
                  "Pages visited and timestamps",
                  "Request data",
                ]}
              />
              <p className="mt-2">
                We do not use this data for tracking or profiling. It is used
                solely for security and operational purposes.
              </p>
            </Sub>
            <Sub title="2.5 Roblox Gameplay Data">
              <p>
                When you play Outfall on Roblox, we may access gameplay-related
                data through Roblox systems, including your Roblox user ID,
                in-game statistics, and progression data. This is governed by
                Roblox's own Privacy Policy.
              </p>
            </Sub>
          </Section>

          <Section title="3. How We Use Your Data">
            <List
              items={[
                "To authenticate you and manage your session when logging in with Discord",
                "To process and review career applications",
                "To receive, review, and respond to ban appeals",
                "To send you Discord direct messages about the status of your application or appeal",
                "To verify staff access permissions using Discord role membership",
                "To operate, maintain, and improve our services",
                "To enforce our rules and protect the safety of our community",
              ]}
            />
          </Section>

          <Section title="4. Legal Basis for Processing (UK GDPR)">
            <p>We process your personal data on the following legal grounds:</p>
            <List
              items={[
                "Legitimate interests — operating and improving our game and community, processing applications and appeals, enforcing community rules",
                "Consent — when you voluntarily submit an application or appeal and authenticate with Discord",
                "Contract — where processing is necessary to provide a service you have requested",
              ]}
            />
          </Section>

          <Section title="5. Data Storage and Processors">
            <p>
              Your data is stored and processed by the following third-party
              services:
            </p>
            <Sub title="Supabase">
              <p>
                We use Supabase (a cloud database and authentication platform)
                to store application data, appeal data, and session tokens. Data
                may be stored on servers within the EU/EEA. Supabase acts as a
                data processor under our instruction.
              </p>
            </Sub>
            <Sub title="Vercel">
              <p>
                Our website is hosted on Vercel. Vercel processes request data
                and may log IP addresses and access logs for security purposes.
              </p>
            </Sub>
            <Sub title="Discord">
              <p>
                We use Discord's OAuth2 service to authenticate users and send
                direct messages via our bot. Discord is an independent data
                controller and its own privacy policy applies to data held by
                them.
              </p>
            </Sub>
            <p className="mt-3">We do not sell your data to any third party.</p>
          </Section>

          <Section title="6. Data Retention">
            <List
              items={[
                "Career applications: retained for 12 months from submission, then deleted or anonymised",
                "Archived applications: retained indefinitely in anonymised form for record-keeping",
                "Ban appeals: retained for 24 months from the date of decision",
                "Session data: deleted automatically after 3 days of inactivity",
                "Discord OAuth data: retained for the duration of your session and purged on logout",
              ]}
            />
          </Section>

          <Section title="7. Cookies">
            <p>
              We use a single session cookie (
              <code className="bg-white/10 px-1 rounded text-sm">
                outfall_session
              </code>
              ) to keep you logged in. This cookie contains a session token, not
              your personal data. It expires after 3 days. We do not use
              advertising or tracking cookies. For full details, see our{" "}
              <a href="/cookie-policy" className="text-[#ffd978] underline">
                Cookie Policy
              </a>
              .
            </p>
          </Section>

          <Section title="8. Children's Privacy">
            <p>
              Outfall is a Roblox experience and may be accessed by users under
              the age of 13. Our website requires Discord authentication, and
              Discord's own age restrictions apply. We do not knowingly collect
              personal data from children under 13 without appropriate consent.
            </p>
            <p>
              If you believe a child under 13 has submitted personal data to us
              without parental consent, please contact us immediately and we
              will delete it.
            </p>
          </Section>

          <Section title="9. Your Rights (UK GDPR)">
            <p>
              If you are located in the UK or EU, you have the following rights
              regarding your personal data:
            </p>
            <List
              items={[
                "Right of access — request a copy of the personal data we hold about you",
                "Right to rectification — ask us to correct inaccurate data",
                "Right to erasure — ask us to delete your personal data",
                "Right to restrict processing — ask us to limit how we use your data",
                "Right to data portability — receive your data in a structured, commonly used format",
                "Right to object — object to processing based on legitimate interests",
              ]}
            />
            <p>
              To exercise any of these rights, contact us through our official
              Discord server. We will respond within 30 days.
            </p>
          </Section>

          <Section title="10. Security">
            <p>
              We take reasonable technical and organisational measures to
              protect your data, including encrypted connections (HTTPS), secure
              session management, and access controls on admin systems. However,
              no internet-based service is completely secure and we cannot
              guarantee absolute security.
            </p>
          </Section>

          <Section title="11. Changes to This Policy">
            <p>
              We may update this Privacy Policy from time to time. The "Last
              updated" date at the top of this page will reflect any changes.
              Continued use of our services after an update constitutes
              acceptance of the revised policy. For significant changes, we will
              notify users through our Discord server.
            </p>
          </Section>

          <Section title="12. Contact">
            <p>
              For any privacy-related concerns, data requests, or questions,
              contact us through our official Discord server at{" "}
              <strong className="text-white/90">outfall.website</strong> or via
              direct message to the Outfall staff team.
            </p>
          </Section>
        </div>
      </section>
    </main>
  );
}
